/*
 * Getters for the [energy_management_systems store]{link module:store/energy_management_systems}
 *
 * @module store/energy_management_systems/getters
 */

import { sortSelectOptions } from '@/components/helpers'
import { SYSTEM_CONFIGURATION_STATUS_FLAG } from '@/store/modules/ems_current_status'

/*
 * Getter to get Information for a single EMS
 *
 * @function
 *
 * @return {function} with EMS-ID as input
 */
export const getEms = (state) => (id) => state.emss[id]

export const filteredEmsIds = (state, _g, _rs, rootGetters) => {
  return ({ filters = {} }) =>
    Object.values(state.emss)
      .filter((ems) => {
        if (filters.nameOrSerialno) {
          const id = parseInt(filters.nameOrSerialno.slice(1, filters.nameOrSerialno.length))
          const isIdHack = filters.nameOrSerialno.startsWith('#') && id

          if (isIdHack) {
            if (ems.id !== id) return false
          } else {
            if (
              !ems.name.toUpperCase().includes(filters.nameOrSerialno.toUpperCase()) &&
              !ems.serialno?.toUpperCase()?.includes(filters.nameOrSerialno.toUpperCase())
            )
              return false
          }
        }

        if (filters.status) {
          const aggregatedStatus = rootGetters['emsCurrentStatus/getEmsCurrentStatus'](ems.id)?.aggregatedStatus
          if (!filters.status.includes(aggregatedStatus)) return false
        }

        if (filters.logEventStatus) {
          const aggregatedStatus = rootGetters['logEvents/getAggregatedLogSourcesStatus'](ems.id)
          if (!filters.logEventStatus.includes(aggregatedStatus)) return false
        }

        const emsCurrentStatus = rootGetters['emsCurrentStatus/getEmsCurrentStatus'](ems.id)
        if (filters.currentStatus) {
          if (!emsCurrentStatus) return false

          if (filters.currentStatus.latestStatus) {
            if (!filters.currentStatus.latestStatus.includes(emsCurrentStatus.latestEmsStatus)) return false
          }
          if (filters.currentStatus.latestEmsAlive) {
            if (emsCurrentStatus.latestEmsAliveAgo > moment.duration(filters.currentStatus.latestEmsAlive)) return false
          }
          if (filters.currentStatus.latestHeartbeat) {
            if (emsCurrentStatus.latestHeartbeatAgo > moment.duration(filters.currentStatus.latestHeartbeat))
              return false
          }
        }

        if (filters.configuration) {
          if (filters.configuration.nonEmptyTopology) {
            if (!filters.configuration.nonEmptyTopology.includes(emsCurrentStatus.nonEmptyTopology)) return false
          }
          if (filters.configuration.systemConfigurationStatus) {
            if (!filters.configuration.systemConfigurationStatus.includes(emsCurrentStatus.systemConfigurationStatus))
              return false
          }
        }

        if (filters.installation) {
          const amperix = rootGetters['amperixes/getAmperix'](ems.id)

          if (filters.installation.emsId) {
            if (filters.installation.emsId !== ems.id) return false
          }
          if (filters.installation.hwVersion) {
            if (!filters.installation.hwVersion.includes(amperix?.hwVersion)) return false
          }
          if (filters.installation.hwSerialNumber) {
            if (!amperix?.hwSerialNumber?.includes(filters.installation.hwSerialNumber)) return false
          }
          if (filters.installation.swVersionInitial) {
            if (!filters.installation.swVersionInitial.includes(amperix?.swVersionInitial)) return false
          }
          if (filters.installation.swVersion) {
            if (!filters.installation.swVersion.includes(amperix?.swVersion)) return false
          }
          if (filters.installation.licenseType) {
            if (!filters.installation.licenseType.includes(amperix?.licenseType)) return false
          }
          if (filters.installation.macAddress) {
            if (!amperix?.macAddress?.includes(filters.installation.macAddress)) return false
          }
          if (filters.installation.productionDate) {
            if (
              !amperix?.productionDate?.isBetween(
                moment(filters.installation.productionDate.from),
                moment(filters.installation.productionDate.to),
                undefined,
                '[]'
              )
            )
              return false
          }
          if (filters.installation.activatedAt) {
            if (
              !emsCurrentStatus?.activatedAt?.isBetween(
                moment(filters.installation.activatedAt.from),
                moment(filters.installation.activatedAt.to),
                undefined,
                '[]'
              )
            )
              return false
          }
          if (filters.installation.putIntoServiceAt) {
            if (
              !emsCurrentStatus?.putIntoServiceAt?.isBetween(
                moment(filters.installation.putIntoServiceAt.from),
                moment(filters.installation.putIntoServiceAt.to),
                undefined,
                '[]'
              )
            )
              return false
          }
        }

        return true
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map((ems) => ems.id)
}

/**
 * Getter to get the EMS-Infos as JSON Obj
 *
 * @function
 *
 * @return {function} with filters as input
 */
export const getEmsInfos = (_s, getters, _rs, rootGetters) => (filters, t) =>
  getters.filteredEmsIds({ filters }).map((emsId) => {
    const ems = getters.getEms(emsId)
    const apx = rootGetters['amperixes/getAmperix'](ems.id)
    const emsCurrentStatus = rootGetters['emsCurrentStatus/getEmsCurrentStatus'](ems.id)
    const locale = rootGetters['user/locale']

    return {
      emsId,
      emsName: ems.name.replaceAll(',', ''),
      serialno: ems.serialno,
      provisionDate: apx?.productionDate ? apx.productionDate.clone().locale(locale).format('l') : '',
      macAddress: apx?.macAddress,
      hwType: apx?.hwVersion,
      hwSerialNumber: apx?.hwSerialNumber,
      // versionOnProd: apx?.swVersionInitial,
      swVersion: apx?.swVersion,
      activatedAt: emsCurrentStatus?.activatedAt ? emsCurrentStatus.activatedAt.clone().locale(locale).format('l') : '',
      putIntoServiceAt: emsCurrentStatus?.putIntoServiceAt
        ? emsCurrentStatus.putIntoServiceAt.clone().locale(locale).format('l')
        : '',
      nonEmptyTopology: t(`api.technician.index.configuration.${emsCurrentStatus?.nonEmptyTopology ? 'yes' : 'no'}`),
      systemConfigurationStatus: t(
        `api.technician.index.configuration.${_.snakeCase(Object.entries(SYSTEM_CONFIGURATION_STATUS_FLAG).find((x) => x[1] === emsCurrentStatus?.systemConfigurationStatus)[0])}`
      )
    }
  })

/**
 * Getter to get the EMS's name
 *
 * @function
 *
 * @return {function} with EMS-ID as input
 */
export const getEmsName = (state) => (id) => state.emss[id]?.name

/*
 * Getter to get all EMSs for a EmsGroup
 * The function returns an {object} including all EMSs
 *
 * @function
 *
 * @return {function}
 */
export const getEmssForGroup = (state) => (id) => {
  if (state.emsGroups[id]) {
    var returnEmss = {}
    for (var emsId of state.emsGroups[id]) {
      if (state.emss[emsId]) {
        returnEmss[emsId] = state.emss[emsId]
      }
    }
    return returnEmss
  } else {
    return {}
  }
}

/*
 * Getter to get all EMSs for a User
 * The function returns an {object} including all EMSs
 *
 * @function
 *
 * @return {function}
 */
export const getEmssForUser = (state) => (id) => {
  if (state.users[id]) {
    var returnEmss = {}
    for (var emsId of state.users[id]) {
      if (state.emss[emsId]) {
        returnEmss[emsId] = state.emss[emsId]
      }
    }
    return returnEmss
  } else {
    return {}
  }
}

/*
 * Getter to get all EMSs for a EmsGroup as Selectoptions
 * The function returns an [] including all EMSs in Group
 *
 * @function
 *
 * @return {function}
 */
export const getEmssForRemoveGroupSelect = (state) => (id) => {
  if (state.emsGroups[id]) {
    var returnEmss = []
    for (var emsId of state.emsGroups[id]) {
      if (state.emss[emsId]) {
        returnEmss.push({
          value: emsId,
          label: state.emss[emsId].name
        })
      }
    }
    return sortSelectOptions(returnEmss, { sortByProperty: 'label' })
  } else {
    return []
  }
}

/*
 * Getter to get all EMSs not in Group as SelectOptions
 * The function returns an [] including all EMSs not in Group
 *
 * @function
 *
 * @return {function}
 */
export const getEmssForAddGroupSelect = (state) => (id) => {
  const groupEmsIds = state.emsGroups[id] ? state.emsGroups[id] : []
  var returnEmss = []

  for (const emsId in state.emss) {
    if (!groupEmsIds.includes(parseInt(emsId))) {
      returnEmss.push({
        value: emsId,
        label: state.emss[emsId].name
      })
    }
  }
  return sortSelectOptions(returnEmss, { sortByProperty: 'label' })
}
